import React, { useState, useEffect, useCallback } from "react"
import { toast } from "sonner"
import {
  walletAPI,
  depositAPI,
  withdrawalAPI,
  transferAPI,
} from "../../utils/api"
import { Card } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Badge } from "../../components/ui/badge"
import { QRCodeSVG } from "qrcode.react"
import {
  Wallet,
  Download,
  Upload,
  Send,
  Copy,
  RefreshCw,
  Search,
  AlertTriangle,
} from "lucide-react"

// Coin icons/colors mapping
const COIN_CONFIG = {
  BTC: {
    name: "Bitcoin",
    icon: "₿",
    color: "#F7931A",
    bgColor: "bg-orange-100",
  },
  ETH: {
    name: "Ethereum",
    icon: "Ξ",
    color: "#627EEA",
    bgColor: "bg-purple-100",
  },
  USDT: {
    name: "Tether USD",
    icon: "₮",
    color: "#26A17B",
    bgColor: "bg-green-100",
  },
  USDC: {
    name: "USD Coin",
    icon: "$",
    color: "#2775CA",
    bgColor: "bg-blue-100",
  },
  BNB: { name: "BNB", icon: "⟠", color: "#F0B90B", bgColor: "bg-yellow-100" },
  XRP: { name: "XRP", icon: "✕", color: "#23292F", bgColor: "bg-gray-100" },
  SOL: {
    name: "Solana",
    icon: "◎",
    color: "#9945FF",
    bgColor: "bg-violet-100",
  },
  ADA: {
    name: "Cardano",
    icon: "₳",
    color: "#0033AD",
    bgColor: "bg-indigo-100",
  },
  DOGE: {
    name: "Dogecoin",
    icon: "Ð",
    color: "#C2A633",
    bgColor: "bg-amber-100",
  },
  LTC: {
    name: "Litecoin",
    icon: "Ł",
    color: "#345D9D",
    bgColor: "bg-slate-100",
  },
  MATIC: {
    name: "Polygon",
    icon: "⬡",
    color: "#8247E5",
    bgColor: "bg-purple-100",
  },
  GBP: {
    name: "British Pound",
    icon: "£",
    color: "#00823B",
    bgColor: "bg-green-100",
  },
  USD: {
    name: "US Dollar",
    icon: "$",
    color: "#2E7D32",
    bgColor: "bg-green-100",
  },
}

const Wallets = () => {
  const [wallets, setWallets] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [depositDialog, setDepositDialog] = useState(false)
  const [withdrawDialog, setWithdrawDialog] = useState(false)
  const [transferDialog, setTransferDialog] = useState(false)
  const [verificationDialog, setVerificationDialog] = useState(false)
  const [verificationType, setVerificationType] = useState(null) // 'withdraw' or 'transfer'
  const [selectedAsset, setSelectedAsset] = useState(null)
  const [amount, setAmount] = useState("")
  const [destinationAddress, setDestinationAddress] = useState("")
  const [recipientEmail, setRecipientEmail] = useState("")
  const [verificationCode, setVerificationCode] = useState("")
  const [pendingWithdrawalId, setPendingWithdrawalId] = useState(null)
  const [pendingTransferId, setPendingTransferId] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [totalBalance, setTotalBalance] = useState(0)

  const fetchWallets = useCallback(async () => {
    try {
      const response = await walletAPI.getAll()
      const walletsData = response.data || []

      console.log("WALLETS", walletsData)
      const validWallets = walletsData.filter(w =>
        w.asset_symbol &&
        w.asset_symbol.trim() !== '' &&
        w.asset_symbol !== 'undefined'
      )
      setWallets(validWallets)

      // Calculate total balance (simplified - would need USD conversion in real app)
      const total = walletsData.reduce((sum, w) => sum + (w.balance || 0), 0)
      setTotalBalance(total)
    } catch (error) {
      toast.error("Failed to load wallets")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchWallets()
  }, [fetchWallets])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchWallets()
    setRefreshing(false)
    toast.success("Wallets refreshed")
  }

  const handleDeposit = async (e) => {
    e.preventDefault()
    try {
      const response = await depositAPI.request({
        asset_symbol: selectedAsset.asset_symbol,
        amount: parseFloat(amount),
        type: selectedAsset.asset_symbol === "USD" ? "fiat" : "crypto",
      })

      if (response.data.wallet_address) {
        toast.success(
          <div className="space-y-2">
            <p>Deposit address generated!</p>
            <code className="text-xs bg-gray-100 p-1 rounded block truncate">
              {response.data.wallet_address}
            </code>
          </div>,
          { duration: 10000 }
        )
      } else {
        toast.success("Deposit request created")
      }
      setDepositDialog(false)
      setAmount("")
      fetchWallets()
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request deposit")
    }
  }

  const handleWithdraw = async (e) => {
    e.preventDefault()
    if (!destinationAddress) {
      toast.error("Please enter a destination address")
      return
    }
    try {
      const response = await withdrawalAPI.request({
        asset_symbol: selectedAsset.asset_symbol,
        amount: parseFloat(amount),
        type: selectedAsset.asset_symbol === "USD" ? "fiat" : "crypto",
        destination: destinationAddress,
      })
      setPendingWithdrawalId(response.data.withdrawal_id)
      setVerificationType("withdraw")
      toast.success("Verification code sent to your email")
      setWithdrawDialog(false)
      setVerificationDialog(true)
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to request withdrawal"
      )
    }
  }

  const handleTransfer = async (e) => {
    e.preventDefault()
    if (!recipientEmail) {
      toast.error("Please enter recipient email")
      return
    }
    try {
      const response = await transferAPI.request({
        asset_symbol: selectedAsset.asset_symbol,
        amount: parseFloat(amount),
        type: selectedAsset.asset_symbol === "USD" ? "fiat" : "crypto",
        recipient_email: recipientEmail,
      })
      setPendingTransferId(response.data.transfer_id)
      setVerificationType("transfer")
      toast.success("Verification code sent to your email")
      setTransferDialog(false)
      setVerificationDialog(true)
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request transfer")
    }
  }

  const handleVerifyWithdrawal = async (e) => {
    e.preventDefault()
    try {
      const response = await withdrawalAPI.verify(
        pendingWithdrawalId,
        verificationCode
      )
      toast.success(
        <div className="space-y-1">
          <p>Withdrawal submitted successfully!</p>
          {response.data?.tx_hash && (
            <p className="text-xs text-gray-500 font-mono">
              Ref: {response.data.tx_hash}
            </p>
          )}
        </div>
      )
      setVerificationDialog(false)
      setVerificationCode("")
      setPendingWithdrawalId(null)
      setVerificationType(null)
      setAmount("")
      setDestinationAddress("")
      fetchWallets()
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code")
    }
  }

  const handleVerifyTransfer = async (e) => {
    e.preventDefault()
    try {
      const response = await transferAPI.verify(
        pendingTransferId,
        verificationCode
      )
      toast.success(
        <div className="space-y-1">
          <p>Transfer completed successfully!</p>
          {response.data?.tx_hash && (
            <p className="text-xs text-gray-500 font-mono">
              Ref: {response.data.tx_hash}
            </p>
          )}
        </div>
      )
      setVerificationDialog(false)
      setVerificationCode("")
      setPendingTransferId(null)
      setVerificationType(null)
      setAmount("")
      setRecipientEmail("")
      fetchWallets()
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code")
    }
  }

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address)
    toast.success("Address copied to clipboard")
  }

  const filteredWallets = (wallets || []).filter((wallet) => {
    if (!searchTerm) return true
    const config = COIN_CONFIG[wallet.asset_symbol] || {}
    const searchLower = searchTerm.toLowerCase()
    return (
      wallet.asset_symbol?.toLowerCase().includes(searchLower) ||
      config.name?.toLowerCase().includes(searchLower)
    )
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #062136;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          /* Transaction button contrast for light mode */
          .tx-action-light {
            background-color: #f1f5f9;
            border-color: #d1d5db;
            color: #be123c;
          }
          .tx-action-light:hover, .tx-action-light:focus {
            background-color: #fff1f2;
            border-color: #be123c;
            color: #991b1b;
          }
          /* Deposit button contrast for light mode */
          .deposit-action-light {
            background-color: #062136;
            color: #fff;
          }
          .deposit-action-light:hover, .deposit-action-light:focus {
            background-color: #0b335a;
            color: #fff;
          }
        `}</style>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fadeIn p-3 sm:p-4 lg:p-6" data-testid="wallets-page">
      <style>{`
        .wallet-withdraw-transfer-btn {
          border-color: #f87171 !important;
          color: #b91c1c !important;
        }
        .wallet-withdraw-transfer-btn:hover {
          background-color: #fef2f2 !important;
          border-color: #ef4444 !important;
          color: #991b1b !important;
        }
        .dark .wallet-withdraw-transfer-btn {
          border-color: #f87171 !important;
          color: #fca5a5 !important;
        }
        .dark .wallet-withdraw-transfer-btn:hover {
          background-color: rgba(127, 29, 29, 0.5) !important;
          border-color: #f87171 !important;
          color: #fecaca !important;
        }
      `}</style>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Wallets
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage your multi-chain cryptocurrency portfolio
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search coins..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Card */}
      <Card className="p-6 bg-gradient-to-r from-[#062136] to-[#1a6b7d]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/70 text-sm">Total Portfolio</p>
            <h2 className="text-3xl font-bold text-white mt-1">
              <span className="text-white">{wallets.length} Wallets</span>
            </h2>
            <p className="text-white/80 text-sm mt-2">
              All wallets are on testnet - perfect for development & testing
            </p>
          </div>
          <Wallet className="w-16 h-16 text-white/20" />
        </div>
      </Card>

      {/* Wallets Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {filteredWallets.length === 0 ? (
          <Card className="p-8 col-span-full text-center">
            <Wallet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No wallets found</p>
            <p className="text-gray-400 text-sm mt-1">
              {searchTerm
                ? "Try a different search term"
                : "Register to create your wallets"}
            </p>
          </Card>
        ) : (
          filteredWallets.map((wallet) => {
            const config = COIN_CONFIG[wallet.asset_symbol] || {
              name: wallet.asset_name || wallet.asset_symbol,
              icon: "●",
              color: "#666",
              bgColor: "bg-gray-100",
            }

            return (
              <Card
                key={wallet.id || wallet.asset_symbol}
                className="p-4 hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700 flex flex-col min-w-0 overflow-hidden"
                data-testid={`wallet-${wallet.asset_symbol}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className={`w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center flex-shrink-0`}
                      style={{ color: config.color }}
                    >
                      <span className="text-xl font-bold">{config.icon}</span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {wallet.asset_symbol}
                      </h3>
                      <p className="text-xs text-gray-500 truncate">{config.name}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="flex-shrink-0 text-xs ml-2">
                    {wallet.network || "Testnet"}
                  </Badge>
                </div>

                {/* Balance */}
                <div className="mb-3">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                    {(wallet.available_balance || 0).toFixed(
                      wallet.asset_symbol === "BTC" ? 8 : 4
                    )}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Total: {(wallet.total_balance || 0).toFixed(4)}
                  </p>
                </div>

                {/* Address */}
                {wallet.wallet_address && (
                  <div className="flex items-center gap-2 my-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg min-w-0">
                    <code className="text-xs text-gray-600 dark:text-gray-300 truncate flex-1 font-mono min-w-0">
                      {wallet.wallet_address}
                    </code>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="flex-shrink-0 h-6 w-6"
                      onClick={() => copyAddress(wallet.wallet_address)}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-1 mt-auto pt-2 w-full">
                  <Dialog
                    open={
                      depositDialog &&
                      selectedAsset?.asset_symbol === wallet.asset_symbol
                    }
                    onOpenChange={(open) => {
                      setDepositDialog(open)
                      if (open) setSelectedAsset(wallet)
                    }}
                  >
                    <DialogTrigger asChild>
                      <button
                        className="flex-1 flex items-center justify-center gap-1 px-1 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors min-w-0 overflow-hidden"
                        data-testid={`deposit-btn-${wallet.asset_symbol}`}
                      >
                        <Download size={11} />
                        <span className="hidden sm:inline truncate">Deposit</span>
                      </button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md w-full p-0 overflow-y-auto max-h-[90vh] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl">
                      {/* Header */}
                      <div className="flex items-center gap-3 p-5 border-b border-gray-100 dark:border-gray-700">
                        <div
                          className={`w-8 h-8 rounded-full ${config.bgColor} flex items-center justify-center`}
                          style={{ color: config.color }}
                        >
                          <span className="text-lg">{config.icon}</span>
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Deposit {wallet.asset_symbol}
                        </h2>
                      </div>

                      {/* Body */}
                      <div className="p-5">
                        <form onSubmit={handleDeposit} className="space-y-4">
                          {wallet.wallet_address && (
                            <>
                              {/* QR Code */}
                              <div className="flex justify-center p-4">
                                <div className="p-3 bg-white rounded-xl border border-gray-200 dark:border-gray-700 inline-block">
                                  <QRCodeSVG
                                    value={wallet.wallet_address}
                                    size={140}
                                    bgColor="#ffffff"
                                    fgColor="#111827"
                                    level="M"
                                  />
                                </div>
                              </div>

                              {/* Address */}
                              <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                                  Your Deposit Address
                                </label>
                                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-3 flex items-center gap-2">
                                  <code className="text-sm text-gray-700 dark:text-gray-200 break-all flex-1 font-mono leading-relaxed">
                                    {wallet.wallet_address}
                                  </code>
                                  <button
                                    type="button"
                                    onClick={() => copyAddress(wallet.wallet_address)}
                                    className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 transition-colors"
                                    title="Copy address"
                                  >
                                    <Copy size={16} />
                                  </button>
                                </div>
                              </div>

                              {/* Network warning */}
                              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                                  Only send <strong>{wallet.asset_symbol}</strong> on the{' '}
                                  <strong>{wallet.network || 'testnet'}</strong> network to this address.
                                  Sending other assets may result in permanent loss.
                                </p>
                              </div>
                            </>
                          )}

                          {/* Amount */}
                          <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Amount <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                            </label>
                            <input
                              type="number"
                              step="0.00000001"
                              value={amount}
                              onChange={(e) => setAmount(e.target.value)}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-3 text-sm mt-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                              placeholder="0.00"
                              data-testid="deposit-amount-input"
                            />
                          </div>

                          {/* Submit */}
                          <button
                            type="submit"
                            className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium py-3 rounded-xl transition-colors mt-2"
                            data-testid="confirm-deposit-button"
                          >
                            Generate Deposit Request
                          </button>
                        </form>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog
                    open={
                      withdrawDialog &&
                      selectedAsset?.asset_symbol === wallet.asset_symbol
                    }
                    onOpenChange={(open) => {
                      setWithdrawDialog(open)
                      if (open) setSelectedAsset(wallet)
                    }}
                  >
                    <DialogTrigger asChild>
                      <button
                        className="flex-1 flex items-center justify-center gap-1 px-1 py-2 border border-red-400 dark:border-red-500 text-red-500 dark:text-red-400 text-xs font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors min-w-0 overflow-hidden"
                        data-testid={`withdraw-btn-${wallet.asset_symbol}`}
                      >
                        <Upload size={11} />
                        <span className="hidden sm:inline truncate">Withdraw</span>
                      </button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <div
                            className={`w-8 h-8 rounded-full ${config.bgColor} flex items-center justify-center`}
                            style={{ color: config.color }}
                          >
                            <span className="text-lg">{config.icon}</span>
                          </div>
                          Withdraw {wallet.asset_symbol}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleWithdraw} className="space-y-4">
                        <div>
                          <Label>Destination Address</Label>
                          <Input
                            type="text"
                            value={destinationAddress}
                            onChange={(e) =>
                              setDestinationAddress(e.target.value)
                            }
                            className="mt-1 font-mono text-sm"
                            placeholder="Enter wallet address"
                            required
                          />
                        </div>
                        <div>
                          <Label>Amount</Label>
                          <Input
                            type="number"
                            step="0.00000001"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="mt-1"
                            placeholder="0.00"
                            max={wallet.available_balance}
                            required
                            data-testid="withdraw-amount-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Available:{" "}
                            {(wallet.available_balance || 0).toFixed(8)}{" "}
                            {wallet.asset_symbol}
                          </p>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-xs text-amber-800">
                            ⚠️ A verification code will be sent to your email
                            for security.
                          </p>
                        </div>
                        <Button
                          type="submit"
                          className="w-full bg-red-600 hover:bg-red-700 text-white"
                          data-testid="confirm-withdraw-button"
                        >
                          Request Withdrawal
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <Dialog
                    open={
                      transferDialog &&
                      selectedAsset?.asset_symbol === wallet.asset_symbol
                    }
                    onOpenChange={(open) => {
                      setTransferDialog(open)
                      if (open) setSelectedAsset(wallet)
                    }}
                  >
                    <DialogTrigger asChild>
                      <button
                        className="flex-1 flex items-center justify-center gap-1 px-1 py-2 border border-orange-400 dark:border-orange-500 text-orange-500 dark:text-orange-400 text-xs font-medium rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors min-w-0 overflow-hidden"
                        data-testid={`transfer-btn-${wallet.asset_symbol}`}
                      >
                        <Send size={11} />
                        <span className="hidden sm:inline truncate">Transfer</span>
                      </button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <div
                            className={`w-8 h-8 rounded-full ${config.bgColor} flex items-center justify-center`}
                            style={{ color: config.color }}
                          >
                            <span className="text-lg">{config.icon}</span>
                          </div>
                          Transfer {wallet.asset_symbol}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleTransfer} className="space-y-4">
                        <div>
                          <Label>Recipient Email</Label>
                          <Input
                            type="email"
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            className="mt-1"
                            placeholder="user@example.com"
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Enter the email address of the recipient
                          </p>
                        </div>
                        <div>
                          <Label>Amount</Label>
                          <Input
                            type="number"
                            step="0.00000001"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="mt-1"
                            placeholder="0.00"
                            max={wallet.available_balance}
                            required
                            data-testid="transfer-amount-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Available:{" "}
                            {(wallet.available_balance || 0).toFixed(8)}{" "}
                            {wallet.asset_symbol}
                          </p>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-xs text-amber-800">
                            ⚠️ A verification code will be sent to your email
                            for security.
                          </p>
                        </div>
                        <Button
                          type="submit"
                          className="w-full bg-red-600 hover:bg-red-700 text-white"
                          data-testid="confirm-transfer-button"
                        >
                          Request Transfer
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

              </Card>
            )
          })
        )}
      </div>

      {/* Verification Code Dialog */}
      <Dialog open={verificationDialog} onOpenChange={setVerificationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Verify{" "}
              {verificationType === "transfer" ? "Transfer" : "Withdrawal"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              A 6-digit verification code has been sent to your email. Please
              enter it below to confirm your{" "}
              {verificationType === "transfer" ? "transfer" : "withdrawal"}.
            </p>
            <form
              onSubmit={
                verificationType === "transfer"
                  ? handleVerifyTransfer
                  : handleVerifyWithdrawal
              }
              className="space-y-4"
            >
              <div>
                <Label>Verification Code</Label>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="mt-1 text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  required
                  data-testid="verification-code-input"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  Code expires in 10 minutes
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#062136] hover:bg-[#062136]/90 text-white"
                data-testid="verify-button"
              >
                Verify &{" "}
                {verificationType === "transfer"
                  ? "Complete Transfer"
                  : "Submit Withdrawal"}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  )
}

export default Wallets
